import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {
  getInsuranceClaims,
  GetInsuranceClaimsParams,
} from '$api/finance/insuranceClaims/insuranceList';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {createInsuranceClaim} from '$api/finance/insuranceClaims/createInsuranceClaim';
import {ToastType} from '~/components/Toast/constant';
import toast from '~/utils/toast';
import {RoutePath} from '~/constants/routes';
import {RouteSection, getRoute} from '~/utils/route';
import {
  getInsuranceClaimDetails,
  updateInsuranceClaim,
  UpdateInsuranceClaimPayload,
  deleteBillingItem,
  deleteInsuranceClaim,
} from '$api/finance/insuranceClaims/insuranceClaimDetails';

export function useInsuranceClaims(params: GetInsuranceClaimsParams = {}) {
  const {data, isLoading, isError, error} = useQuery({
    queryKey: ['insurance-claims', params],
    queryFn: () => getInsuranceClaims(params),
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  return {
    data,
    isLoading,
    isError,
    error,
  };
}

export function useInsuranceClaimsCounts(
  params: GetInsuranceClaimsParams = {}
) {
  const {data, isLoading, isError, error} = useQuery({
    queryKey: ['insurance-claims-counts', params],
    queryFn: () => getInsuranceClaims({...params, perPage: 1000}),
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    enabled: true,
  });

  const counts = {
    all_claims_count: data?.all_claims_count || 0,
    new_claims_count: data?.new_claims_count || 0,
    pending_claims_count: data?.pending_claims_count || 0,
    submitted_claims_count: data?.submitted_claims_count || 0,
    rejected_claims_count: data?.rejected_claims_count || 0,
    paid_claims_count: data?.paid_claims_count || 0,
    outstading_claims_count: data?.outstading_claims_count || 0,
  };

  return {
    counts,
    isLoading,
    isError,
    error,
  };
}

interface UseNewInsuranceClaimReturn {
  isLoading: boolean;
  createInsuranceClaim: (patientId: number) => Promise<void>;
}

export const useNewInsuranceClaim = (): UseNewInsuranceClaimReturn => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const createClaim = async (patientId: number) => {
    try {
      setIsLoading(true);
      const response = await createInsuranceClaim({
        insurance_claim: {
          patient_id: patientId,
        },
      });

      if (response.success) {
        navigate(
          getRoute(
            RouteSection.Finance,
            `${RoutePath.staff.finance.insuranceClaimDetails.replace(':id', response.data.insurance_claim.id.toString())}`
          )
        );
      }
    } catch (error) {
      console.error('Error creating new insurance claim:', error);
      toast(
        ToastType.Error,
        'Insurance claim creation failed',
        error instanceof Error ? error.message : 'An unknown error occurred'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    createInsuranceClaim: createClaim,
  };
};

export function useClaimDetails(id?: string) {
  const {data, isLoading, isError, error} = useQuery({
    queryKey: ['insurance-claim-details', id],
    queryFn: () => getInsuranceClaimDetails(id!),
    enabled: !!id,
  });

  return {
    data: data?.data?.insurance_claim,
    isLoading,
    isError,
    error,
  };
}

export const useUpdateInsuranceClaim = () => {
  const queryClient = useQueryClient();
  const [disableSubmit, setDisableSubmit] = useState(false);

  const {mutate, isPending} = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateInsuranceClaimPayload;
    }) => updateInsuranceClaim(id, payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['insurance-claim-details', variables.id],
      });
      return toast(ToastType.Success, 'Update insurance claim', data.message);
    },
    onError: () => {
      toast(
        ToastType.Error,
        'Update insurance claim',
        'Failed to update claim'
      );
    },
    onSettled: () => setDisableSubmit(false),
  });

  const sendUpdateInsuranceClaimRequest = (
    id: string,
    payload: UpdateInsuranceClaimPayload
  ) => {
    setDisableSubmit(true);
    return new Promise<void>((resolve, reject) => {
      mutate(
        {id, payload},
        {
          onSuccess: () => resolve(),
          onError: error => reject(error),
        }
      );
    });
  };

  return {
    isPending,
    disableSubmit,
    sendUpdateInsuranceClaimRequest,
  };
};

export const useDeleteBillingItem = () => {
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);

  const {mutate, isPending} = useMutation({
    mutationFn: ({itemId}: {itemId: string}) => deleteBillingItem(itemId),
    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: ['insurance-claim-details'],
      });
      return toast(ToastType.Success, 'Delete billing item', data.message);
    },
    onError: data => {
      toast(
        ToastType.Error,
        'Delete billing item',
        data.message || 'Failed to delete billing item'
      );
    },
    onSettled: () => setIsDeleting(false),
  });

  const sendDeleteBillingItemRequest = (itemId: string) => {
    setIsDeleting(true);
    mutate({itemId});
  };

  return {
    isPending,
    isDeleting,
    sendDeleteBillingItemRequest,
  };
};

export const useDeleteInsuranceClaim = () => {
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);

  const {mutate, isPending} = useMutation({
    mutationFn: ({id}: {id: string}) => deleteInsuranceClaim(id),
    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: ['insurance-claims'],
      });
      return toast(ToastType.Success, 'Delete insurance claim', data.message);
    },
    onError: data => {
      toast(
        ToastType.Error,
        'Delete insurance claim',
        data.message || 'Failed to delete insurance claim'
      );
    },
    onSettled: () => setIsDeleting(false),
  });

  const sendDeleteInsuranceClaimRequest = (id: string) => {
    setIsDeleting(true);
    mutate({id});
  };

  return {
    isPending,
    isDeleting,
    sendDeleteInsuranceClaimRequest,
  };
};
